import { ApolloClient, InMemoryCache, gql } from "@apollo/client";
import { getOrganisationDetails, getOrganisationId } from "../organisations/organisationsSlice";
import StorageService from "../../util/storage";
import { isArray } from "@apollo/client/utilities";

const client = new ApolloClient({
  uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
  cache: new InMemoryCache(),
  headers: {
    Authorization: `${StorageService.getItem("bearer")}`,
  },
});

export const fetchShiftbats = async (userId) => {
  const org = await getOrganisationDetails();
  let allShiftbats = [];
  let pagination = {};
  let hasMore = true;

  // Prepare initial pagination for all regions
  org.Regions?.forEach((region) => {
    pagination[region.region_id] = {
      indexName: "UpdatedAtIdx",
      keyExpression: "updatedAt > :lastUpdated",
      keyExpressionValue: "lastUpdated|0|i", // Start from 0 (or earliest)
    };
  });

  while (hasMore) {
    const shiftbats = [];
    const nextPageInfo = {};
    const promises = [];
    const regionIds = [];

    org.Regions?.forEach((region) => {
      // Pagination params for this region
      const regionPagination = pagination[region.region_id] || {};
      // console.log('>>>> regionPagination', regionPagination)
      const baseUrl =
        `${process.env.REACT_APP_SHIFTBATS_URL}/driverable/shifts`;
      const params = new URLSearchParams({
        _k: region.region_id,
      });
      // if (regionPagination.indexName) params.append("indexName", regionPagination.indexName);
      // if (regionPagination.keyExpression) params.append("keyExpression", regionPagination.keyExpression);
      // if (regionPagination.keyExpressionValue) params.append("keyExpressionValue", regionPagination.keyExpressionValue);
      const url = `${baseUrl}?${params.toString()}`;
      promises.push(fetch(url));
      regionIds.push(region.region_id);
    });

    const shiftbatPromises = [];
    await Promise.all(promises).then((responses) => {
      responses.forEach((response, idx) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch driver duties for ${org.organisationId}`);
        }
        shiftbatPromises.push(response.json().then((data) => ({ data, regionIdx: idx })));
      });
    });

    await Promise.all(shiftbatPromises).then((results) => {
      results.forEach(({ data: shiftbatResponse, regionIdx }) => {
        const region = org.Regions[regionIdx];
        // If the API returns pagination info, extract it here
        if (shiftbatResponse.nextPageToken || shiftbatResponse.lastUpdated) {
          nextPageInfo[region.region_id] = {
            nextPageToken: shiftbatResponse.nextPageToken,
            lastUpdated: shiftbatResponse.lastUpdated,
          };
        }
        // Always extract items from shiftbatResponse.items
        const items = isArray(shiftbatResponse) ? shiftbatResponse : shiftbatResponse.items;
        items.forEach((shiftbat) => {
          if (shiftbat.published)
            shiftbats.push({
              ...shiftbat,
              region: region,
            });
        });
      });
    });

    allShiftbats = allShiftbats.concat(shiftbats);

    hasMore = false;
    const newPagination = {};
    for (const [regionId, pageInfo] of Object.entries(nextPageInfo)) {
      if (pageInfo && pageInfo.lastUpdated) {
        hasMore = true;
        newPagination[regionId] = {
          indexName: "UpdatedAtIdx",
          keyExpression: "updatedAt > :lastUpdated",
          keyExpressionValue: `lastUpdated|${pageInfo.lastUpdated}|i`,
        };
      }
    }
    pagination = newPagination;
  }

  return allShiftbats;

  // const query = gql`
  //     query getShiftbats($id: String) {
  //         shiftbats(filters: { organisation: { orgId: { eq: $id } } }) {
  //             data {
  //                 id
  //                 attributes {
  //                     shiftbats {
  //                         name
  //                         file {
  //                             data {
  //                                 attributes {
  //                                     url
  //                                 }
  //                             }
  //                         }
  //                         categories {
  //                             data {
  //                                 attributes {
  //                                     name
  //                                 }
  //                             }
  //                         }
  //                     }
  //                 }
  //             }
  //         }
  //     }
  // `;
  // const { data } = await client.query({
  //     query,
  //     variables: { id: orgId },
  // });
  // return data.shiftbats.data;
};

// Helper to fetch all shiftbats for all regions, paginating until all data is fetched
export const fetchAllShiftbats = async (userId) => {
  let allShiftbats = [];
  let pagination = {};
  let hasMore = true;
  let firstRun = true;
  let org = await getOrganisationDetails();

  // Prepare initial pagination for all regions
  org.Regions?.forEach((region) => {
    pagination[region.region_id] = {
      indexName: "UpdatedAtIdx",
      keyExpression: "updatedAt > :lastUpdated",
      keyExpressionValue: "lastUpdated|0|i", // Start from 0 (or earliest)
    };
  });

  while (hasMore) {
    const { shiftbats, nextPageInfo } = await fetchShiftbats(userId, pagination);
    allShiftbats = allShiftbats.concat(shiftbats);

    hasMore = false;
    const newPagination = {};
    for (const [regionId, pageInfo] of Object.entries(nextPageInfo)) {
      if (pageInfo && pageInfo.lastUpdated) {
        hasMore = true;
        newPagination[regionId] = {
          indexName: "UpdatedAtIdx",
          keyExpression: "updatedAt > :lastUpdated",
          keyExpressionValue: `lastUpdated|${pageInfo.lastUpdated}|i`,
        };
      }
    }
    pagination = newPagination;
  }

  return allShiftbats;
};
